import React, { useEffect, useState } from 'react';
import { useStowageStore } from 'src/context/StowageContext';
import OdfjellPanel, { PANEL_CONSTANTS } from 'src/components/ui/odfjellPanel/OdfjellPanel';
import { RotationCargoOperation } from 'src/types/ViewModel/Stowage/Rotation/RotationCargoOperation';
import { Stowage } from 'src/types/ViewModel/Stowage/Stowage';
import { getCargoOperationForCargo } from 'src/utils/stowage/stowageRotationUtils';
import { PanelContent, PanelSectionContent, PanelSubHeader } from 'src/components/ui/odfjellPanel/OdfjellPanel.styles';
import { stowedWeightInTanks, stowedVolume } from 'src/utils/stowage/stowageOperation/stowageOperationUtils';
import { calculateVolume } from 'src/utils/stowage/stowageOperation/volumeUtils';
import { getCargoTemperature } from 'src/utils/stowage/stowageOperation/cargoTemperatureUtils';
import Decimal from 'decimal.js';
import useStowageQueryParamsUtils from 'src/utils/stowage/useStowageQueryParamsUtils';
import { RotationCargoOperationVM, OperationWithLocation } from 'src/utils/stowage/stowageOperation/operationFormatUtils';
import { NoOperationsMessage, OperationsTable } from './OperationsForCargoPanel.styles';
import { useStowageSelectionStore } from 'src/context/StowageSelectionContext';
import CargoOverviewSection from './CargoOverviewSection';
import OptionsBarSection from './OptionsBarSection';
import OperationRow from './OperationRow';
import { TableHeader, LoadingHeader, UnloadingHeader, TransitHeader } from './TableComponents';

const OperationsForCargoPanel: React.FC = () => {
    const [cargoOperationsList, setCargoOperationsList] = useState<RotationCargoOperationVM[] | null>(null);
    const [showTransit, setShowTransit] = useState<boolean>(false);
    const [showStowageItems, setShowStowageItems] = useState<boolean>(false);
    const [isCargoDetailsExpanded, setIsCargoDetailsExpanded] = useState<boolean>(false);
    const { activeStowagePlan, isShowOperationForCargoPanelOpen, setIsShowOperationForCargoPanelOpen } = useStowageStore();
    const { selectedCargoOperation } = useStowageSelectionStore();
    const { setQueryParamStowagePortCall, setQueryParamStowageBerthCall } = useStowageQueryParamsUtils();

    useEffect(() => {
        if (!activeStowagePlan || !selectedCargoOperation) return;
        setInitialCargoOperation(selectedCargoOperation);
    }, [selectedCargoOperation, activeStowagePlan]);

    const setInitialCargoOperation = (cargoOperation: RotationCargoOperation) => {
        const _originalOperations: RotationCargoOperation[] = getCargoOperationForCargo(activeStowagePlan, cargoOperation.cargo);

        // Create simplified VM objects with only necessary properties
        const _cargoOperationsList: RotationCargoOperationVM[] = _originalOperations.map((op, index) => {
            // Calculate stowed weight and volume
            const _stowedWeight = stowedWeightInTanks(activeStowagePlan, op);
            const _stowedVolume = stowedVolume(activeStowagePlan, op);

            // Create a new RotationCargoOperationVM with only the properties we need
            return {
                cargoOperationGuid: op.cargoOperationGuid,
                portCallGuid: op.portCallGuid,
                berthCallGuid: op.berthCallGuid,
                cargo: op.cargo,
                portDisplayName: op.berthCall?.portCall?.portDisplayName || 'Unknown Port',
                berthName: op.berthCall?.berthName || 'Unknown Berth',
                cargoOperationSequenceNumber: op.cargoOperationSequenceNumber,
                cargoOperationOrderNumber: index + 1,
                nominatedWeight: op.nominatedWeight * (op.operationType === 'Unloading' ? -1 : 1), // Adjust for unloading
                billOfLadingWeight: op.billOfLadingWeight, // Set the bill of lading weight
                operationType: op.operationType,
                stowedWeight: _stowedWeight.toNumber(),
                stowedVolume: _stowedVolume.toNumber(),
                optionType: op.optionType,
                optionMinusPercentage: op.optionMinusPercentage,
                optionPlusPercentage: op.optionPlusPercentage,
                orgCargoOperation: op,
            };
        });

        setCargoOperationsList([..._cargoOperationsList]);
    };

    // Get all operations in chronological order, filtering transit operations if needed
    const getAllOperationsInOrder = (operations: RotationCargoOperationVM[]): OperationWithLocation[] => {
        if (!operations) return [];

        // Create location info for all operations
        const operationsWithLocation = operations.map((op) => {
            // Calculate not stowed values based on operation type
            let notStowedWeightVal: Decimal | null = null;
            let notStowedVolumeVal: Decimal | null = null;

            if (op.operationType === 'Loading') {
                // For loading operations, calculate not stowed weight
                const _nominatedWeight = new Decimal(op.nominatedWeight || 0);
                const _stowedWeight = new Decimal(op.stowedWeight || 0);
                notStowedWeightVal = _nominatedWeight.minus(_stowedWeight);

                // Calculate volume based on not stowed weight
                if (notStowedWeightVal.gt(0)) {
                    notStowedVolumeVal = calculateVolume(notStowedWeightVal, op.cargo, getCargoTemperature(activeStowagePlan, op.cargo));
                } else {
                    notStowedVolumeVal = new Decimal(0);
                }
            }

            return {
                operation: op,
                portDisplayName: op.portDisplayName, // Use the port name directly from our view model
                berthName: op.berthName || 'Unknown Berth',
                stowedWeight: new Decimal(op.stowedWeight || 0),
                stowedVolume: new Decimal(op.stowedVolume || 0),
                notStowedWeight: notStowedWeightVal,
                notStowedVolume: notStowedVolumeVal,
            };
        });

        // Filter out transit operations if showTransit is false
        const filteredOperations = showTransit
            ? operationsWithLocation
            : operationsWithLocation.filter((item) => item.operation.operationType !== 'Transit');

        // Sort operations by cargoOperationOrderNumber
        return filteredOperations.sort((a, b) => {
            if (a.operation.cargoOperationOrderNumber !== undefined && b.operation.cargoOperationOrderNumber !== undefined) {
                return a.operation.cargoOperationOrderNumber - b.operation.cargoOperationOrderNumber;
            }

            // Fallback to sequence number if cargoOperationOrderNumber isn't available
            if (a.operation.cargoOperationSequenceNumber !== undefined && b.operation.cargoOperationSequenceNumber !== undefined) {
                return a.operation.cargoOperationSequenceNumber - b.operation.cargoOperationSequenceNumber;
            }

            return 0;
        });
    };

    // Get cargo details including option information
    const getCargoDetails = () => {
        if (!cargoOperationsList || cargoOperationsList.length === 0) {
            return null;
        }

        // Get the cargo from the first operation
        const cargo = cargoOperationsList[0]?.cargo;
        if (!cargo) {
            return null;
        }

        // Get option details from cargo or cargo operation
        const optionType = cargo.optionType || cargoOperationsList[0].optionType;
        const optionMinusPercentage = cargo.optionMinusPercentage || cargoOperationsList[0].optionMinusPercentage;
        const optionPlusPercentage = cargo.optionPlusPercentage || cargoOperationsList[0].optionPlusPercentage;

        return {
            tradeName: cargo?.product?.tradeName || 'Unknown',
            optionType,
            optionMinusPercentage,
            optionPlusPercentage,
        };
    };

    const cargoDetails = getCargoDetails();

    // Format the cargo number for display in the header
    const getHeaderTitle = () => {
        return 'Operations for cargo';
    };

    // Format option details for display
    const getFormattedOptionDetails = () => {
        if (!cargoDetails || !cargoDetails.optionType) return 'None';

        let result = cargoDetails.optionType;

        if (cargoDetails.optionMinusPercentage || cargoDetails.optionPlusPercentage) {
            const minus = cargoDetails.optionMinusPercentage !== null ? cargoDetails.optionMinusPercentage + '%' : '0%';
            const plus = cargoDetails.optionPlusPercentage !== null ? cargoDetails.optionPlusPercentage + '%' : '0%';
            result += ` (${minus}/${plus})`;
        }

        return result;
    };

    const toggleCargoDetails = () => {
        setIsCargoDetailsExpanded(!isCargoDetailsExpanded);
    };

    const onClose = () => {
        setIsShowOperationForCargoPanelOpen(null);
    };

    // Helper function to get previous operation
    const getPreviousOperation = (index: number, array: OperationWithLocation[]): RotationCargoOperationVM | null => {
        return index > 0 ? array[index - 1].operation : null;
    };

    // Helper function to get stowage items for an operation
    const getStowageItemsForOperation = (operation: RotationCargoOperationVM) => {
        if (!activeStowagePlan || !operation.orgCargoOperation) return [];

        const baseFilter = (s: Stowage) => s.cargoOperationGuid === operation.orgCargoOperation.cargoOperationGuid;
        const transferFilter = (s: Stowage) => operation.orgCargoOperation.operationType !== 'Transfer' || s.weight.greaterThan(0);
        const filter = (s: Stowage) => baseFilter(s) && transferFilter(s);

        return activeStowagePlan.stowageItems.filter(filter);
    };

    // Early return if panel is closed
    if (!isShowOperationForCargoPanelOpen) {
        return null;
    }

    // Early return if no cargo operations
    if (!cargoOperationsList || cargoOperationsList.length === 0) {
        return (
            <OdfjellPanel
                headerTitle={getHeaderTitle()}
                defaultPosition="center"
                panelWidth={PANEL_CONSTANTS.VERY_LARGE_WIDTH}
                actionButtonText="OK"
                preventOutsideClose={true}
                onAction={onClose}
                onCancel={onClose}
            >
                <PanelContent>
                    <NoOperationsMessage>No cargo operations available</NoOperationsMessage>
                </PanelContent>
            </OdfjellPanel>
        );
    }

    const operations = getAllOperationsInOrder(cargoOperationsList);

    return (
        <OdfjellPanel
            headerTitle={getHeaderTitle()}
            defaultPosition="center"
            panelWidth={PANEL_CONSTANTS.VERY_LARGE_WIDTH}
            actionButtonText="OK"
            preventOutsideClose={true}
            onAction={onClose}
            onCancel={onClose}
        >
            <PanelContent>
                <CargoOverviewSection
                    cargoOperationsList={cargoOperationsList}
                    isExpanded={isCargoDetailsExpanded}
                    onToggle={toggleCargoDetails}
                    tradeName={cargoDetails?.tradeName || 'Unknown Cargo'}
                    optionDetails={getFormattedOptionDetails()}
                />

                <PanelSectionContent>
                    <PanelSubHeader>Operations</PanelSubHeader>

                    <OperationsTable>
                        <TableHeader />

                        {operations.map((item, index, array) => {
                            const prevOperation = getPreviousOperation(index, array);
                            const stowageItems = getStowageItemsForOperation(item.operation);

                            return (
                                <React.Fragment key={`operation_${item.operation.cargoOperationOrderNumber}_${item.operation.cargoOperationGuid}`}>
                                    <LoadingHeader operation={item.operation} prevOperation={prevOperation} />
                                    <UnloadingHeader operation={item.operation} prevOperation={prevOperation} />
                                    <TransitHeader operation={item.operation} prevOperation={prevOperation} />

                                    <OperationRow
                                        item={item}
                                        prevOperation={prevOperation}
                                        showStowageItems={showStowageItems}
                                        activeStowagePlan={activeStowagePlan}
                                        stowageItems={stowageItems}
                                        onPortClick={setQueryParamStowagePortCall}
                                        onBerthClick={setQueryParamStowageBerthCall}
                                    />
                                </React.Fragment>
                            );
                        })}
                    </OperationsTable>

                    <OptionsBarSection
                        showTransit={showTransit}
                        showStowageItems={showStowageItems}
                        onToggleTransit={setShowTransit}
                        onToggleStowageItems={setShowStowageItems}
                    />
                </PanelSectionContent>
            </PanelContent>
        </OdfjellPanel>
    );
};

export default OperationsForCargoPanel;
